// THE PROJECTION CONSOLE (tab-portal-admin).
//
// This page's output is a CLAIM ABOUT WHAT A CLIENT CAN SEE. That is what makes it different
// from every other tab, and it is why nothing on it may be generated, estimated or guessed: the
// rail, the projection, the ranked moves, the per-person capability line and every warning are
// pure functions of database state, reproducible byte-for-byte for the same state (§0).
//
// The old page answered a different question. It showed the operator the four INPUTS — a master
// toggle, a 10×3 radio matrix, nine group chips, a publish table — and never once the OUTPUT.
// So "what does the client actually see?" could only be answered by minting a one-time preview
// token and opening a second tab, which is why the audit log holds 27 preview tokens against
// 4 publishes: the token is consumed on first use, so re-checking after a change cost a fresh
// one every time. §3.4 made the link redeemable for its whole TTL; this page makes it mostly
// unnecessary, because the projection is computed here from the same three gates the portal
// applies — engagement switch, object-type visibility, row flag.
//
// FIVE ZONES under a five-cell rail:
//   1  NEXT MOVE                  — eight deterministic rules, ranked (ladder.ts)
//   2  WHAT THE CLIENT WOULD FIND — the seven views resolved, beside a DAY-PINNED miniature
//   3  WHAT IS SHARED             — ten type sections; the visibility select lives in the header
//                                   of the section it governs, and row toggles STAGE
//   4  WHO CAN SIGN IN            — accounts, or the client's contacts when there are none
//   5  CONVERSATION               — each thread carrying the statement it is about
//
// THREE DEFECTS THIS PAGE FIXES, not just re-dresses:
//   · "Publish all in scope" filtered `excluded` and never consulted `status`, so a DRAFT
//     statement rode along into the client's portal. The verb is now "Share every held row in
//     scope" and it means it.
//   · `ACT_MEANS.deliverables = 'comment'` promised a surface that does not exist. The strings
//     now come from src/shared/portalViews.ts, which the portal itself renders from.
//   · A publish was written the instant a toggle moved. Toggles now stage, the projection
//     recomputes against the staged set, and the operator sees the consequence BEFORE the write.
//
// Every mutation still goes through the endpoint that already owned it.

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useEngagement } from '../context/EngagementContext.js';
import { useToast } from '../components/Toast.js';
import ConfirmModal from '../components/ConfirmModal.js';
import EmptyState, { ProblemState } from '../components/EmptyState.js';
import { usePoll } from '../hooks/usePoll.js';
import { ROUTES, scrollToAnchor, useDeepLink, useOpenRoute, useRouteParams } from '../routes.js';
import { cutRun, degradedRail, type RailCell, type RailTone } from '../../shared/rail.js';
import { hasProblem, problemSentence } from '../../shared/problems.js';
import { REQUIREMENT_STATUS_LABEL } from '../../shared/requirementStatus.js';
import { useT, useLocale } from '../i18n/index.js';
import { tCtx } from '../../shared/i18n.js';
import {
  PORTAL_SURFACES, VISIBILITIES, type PortalObjectType, type Visibility,
} from '../../shared/portalViews.js';
import {
  portalAdminApi,
  type InviteResult, type PortalComment, type PortalRole, type PortalUser,
  type Projection, type ProjectionView, type PublishableGroup, type PublishableResponse,
  type PublishableRow,
} from '../api/portalAdmin.js';
import { getClient, type Contact, type DeletionPlan } from '../api/crm.js';
import { ALL_CLEAR, portalMoves, type PortalMove } from './portalAdmin/ladder.js';
import './PortalAdminPage.de.js';
import './PortalAdminPage.fr.js';

type T = ReturnType<typeof useT>;

/** The tab's existing cadence. CLIENT LAST SEEN is the one figure that moves on its own. */
const POLL_MS = 20_000;

// ────────────────────────────────────────────────────────────────────────────
// The rail
// ────────────────────────────────────────────────────────────────────────────

type PaRailKey = 'REACHABLE' | 'SHARED' | 'CAN SIGN IN' | 'CLIENT LAST SEEN' | 'AWAITING YOU';
const RAIL_KEYS: readonly PaRailKey[] = [
  'REACHABLE', 'SHARED', 'CAN SIGN IN', 'CLIENT LAST SEEN', 'AWAITING YOU',
];

/** This rail's narrowing of the shared cell (BL-5) — same shape, this tab's key union. */
interface PaRailCell extends RailCell { key: PaRailKey }

const TONE_CLASS: Partial<Record<RailTone, string>> = {
  warn: ' fv--attend',
  attend: ' fv--attend',
  ok: ' fv--ok',
  nil: ' fv--nil',
  err: ' fv--attend',
  broken: ' fv--attend',
};

// ────────────────────────────────────────────────────────────────────────────
// The ten type sections
// ────────────────────────────────────────────────────────────────────────────

/**
 * LAW 1 — typeface encodes provenance, per type:
 *   record  a statement a human or the agent wrote, which the client reads as one → SERIF,
 *           and it takes a claim mark
 *   label   a name we gave a thing → SANS, no mark
 *   ref     an identifier (a filename) → MONO, no mark
 *
 * The first build of this page set document filenames in serif. A filename is checkable
 * identity, not the record, and that was a LAW 1 break.
 */
type Voice = 'record' | 'label' | 'ref';

interface Section {
  type: PortalObjectType;
  /** The key in the /publishable response, or null when the type has no per-row flag. */
  group: string | null;
  voice: Voice;
}

const SECTIONS: Section[] = [
  { type: 'requirements', group: 'requirements', voice: 'record' },
  { type: 'acceptance_criteria', group: 'acceptance_criteria', voice: 'record' },
  { type: 'deliverables', group: 'deliverables', voice: 'label' },
  { type: 'documents', group: 'documents', voice: 'ref' },
  { type: 'test_cases', group: 'test_cases', voice: 'record' },
  { type: 'milestones', group: 'milestones', voice: 'label' },
  { type: 'sows', group: 'sow_versions', voice: 'label' },
  { type: 'change_requests', group: 'change_requests', voice: 'record' },
  { type: 'acceptance_records', group: 'acceptance_records', voice: 'label' },
  { type: 'defects', group: null, voice: 'record' },
];

const VIS_LABEL: Record<Visibility, string> = {
  hidden: 'hidden', read: 'read', read_act: 'read + act',
};

/** How many rows a section shows before the operator asks for the rest. */
const ROW_WINDOW = 12;

/** LAW 6 — the claim mark. Computed tallies never take one; nor do exclusions. */
function markOf(voice: Voice, row: PublishableRow): string | null {
  if (voice !== 'record') return null;
  if (row.excluded) return null;
  return row.status === 'draft' || row.status === 'proposed' ? 'att--proposed' : 'att--held';
}

/**
 * Does this row count toward what the CLIENT would see? For seven types the publish flag is the
 * whole answer; for three the portal applies one more predicate, and a console that ignored them
 * would print a number the client's own screen contradicts.
 */
function countsInPortal(type: PortalObjectType, row: PublishableRow): boolean {
  if (type === 'test_cases') return row.status === 'ready';
  if (type === 'acceptance_criteria') return row.status !== 'dropped';
  if (type === 'documents') return row.status !== 'failed';
  return true;
}

const firstName = (who: string): string => who.split(' ')[0] ?? who;

const Fig = ({ children }: { children: React.ReactNode }) => <span className="voice-figure">{children}</span>;
const Ref = ({ children }: { children: React.ReactNode }) => <span className="src-ref">{children}</span>;

// ────────────────────────────────────────────────────────────────────────────

interface Detail {
  publishable: PublishableResponse;
  users: PortalUser[];
  comments: PortalComment[];
  contacts: Contact[];
}

export default function PortalAdminPage() {
  const nav = useEngagement();
  const { current, engagements } = nav;
  const openRoute = useOpenRoute(nav);
  const params = useRouteParams();
  const toast = useToast();
  const t = useT();
  const { tp, fmtDate, fmtDateTime } = useLocale();
  const engagementId = current?.id ?? null;
  const clientId = current?.client_id ?? null;

  const err = useCallback(
    (e: unknown, fallback: string) => toast('err', e instanceof Error ? e.message : fallback),
    [toast],
  );

  // ── the one poll ──────────────────────────────────────────────────────────
  const loadProjection = useCallback(async (): Promise<Projection | null> => {
    if (!engagementId) return null;
    return portalAdminApi.projection(engagementId);
  }, [engagementId]);
  const poll = usePoll(loadProjection, POLL_MS);
  const projection = poll.data;

  // ── the row lists, fetched on arrival and after every write (never polled) ─
  const [detail, setDetail] = useState<Detail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!engagementId || !clientId) return;
    setDetailLoading(true);
    try {
      const [publishable, users, comments, client] = await Promise.all([
        portalAdminApi.publishable(engagementId),
        portalAdminApi.users(engagementId),
        portalAdminApi.comments(engagementId),
        getClient(clientId),
      ]);
      setDetail({ publishable, users, comments, contacts: client.contacts ?? [] });
      setDetailError(null);
    } catch (e) {
      setDetailError(e instanceof Error ? e.message : t('The publish lists could not be read.'));
    } finally {
      setDetailLoading(false);
    }
  }, [engagementId, clientId, t]);

  useEffect(() => { setDetail(null); void loadDetail(); }, [loadDetail]);

  const refreshAll = useCallback(async () => {
    await Promise.all([poll.refresh(), loadDetail()]);
  }, [poll, loadDetail]);

  // ── page-local state ──────────────────────────────────────────────────────
  const [open, setOpen] = useState<Set<PortalObjectType>>(() => new Set(['requirements'] as PortalObjectType[]));
  const [expanded, setExpanded] = useState<Set<PortalObjectType>>(() => new Set());
  const [staged, setStaged] = useState<Map<string, boolean>>(() => new Map());
  const [previewTab, setPreviewTab] = useState<string>('overview');
  const [showRest, setShowRest] = useState(false);
  const [openThread, setOpenThread] = useState<string | null>(null);
  const [pendingThread, setPendingThread] = useState<string | null>(null);
  const [scrollThread, setScrollThread] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState<Record<string, string>>({});
  const [replyBusy, setReplyBusy] = useState<string | null>(null);
  const [invites, setInvites] = useState<Record<number, InviteResult>>({});
  const [grantUser, setGrantUser] = useState<PortalUser | null>(null);
  const [newFrom, setNewFrom] = useState<{ contact: Contact | null; name: string; email: string; role: PortalRole } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ user: PortalUser; plan: DeletionPlan } | null>(null);
  const [removing, setRemoving] = useState(false);
  const [confirmStage, setConfirmStage] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const [busy, setBusy] = useState(false);

  // §3.8 — the notification carries the commented object's id; the thread opens and scrolls.
  useDeepLink(params, { thread: (v) => setPendingThread(v) });

  // ── derivations ───────────────────────────────────────────────────────────

  const groups = detail?.publishable.objects ?? null;

  const groupOf = useCallback(
    (key: string | null): PublishableGroup | null => (key && groups ? groups[key] ?? null : null),
    [groups],
  );

  const rowsOf = useCallback(
    (key: string | null): PublishableRow[] => groupOf(key)?.rows ?? [],
    [groupOf],
  );

  const isPub = useCallback(
    (group: string, row: PublishableRow): boolean => {
      const k = `${group}:${row.id}`;
      return staged.has(k) ? staged.get(k)! : row.published;
    },
    [staged],
  );

  /**
   * What the staged set would change, per object type. The COMMITTED figure comes from the
   * server and holds the cell; this is the movement shown beside it in the interaction colour.
   * A staged change is not a fact yet, and the page never lets it look like one.
   */
  const deltas = useMemo(() => {
    const out = new Map<PortalObjectType, { shared: number; visible: number }>();
    if (!groups) return out;
    for (const [key, want] of staged) {
      const [group, rawId] = key.split(':');
      const g = groups[group];
      if (!g) continue;
      const row = g.rows.find((r) => r.id === Number(rawId));
      if (!row || row.published === want) continue;
      const type = g.object_type;
      const cur = out.get(type) ?? { shared: 0, visible: 0 };
      const step = want ? 1 : -1;
      cur.shared += step;
      if (countsInPortal(type, row)) cur.visible += step;
      out.set(type, cur);
    }
    return out;
  }, [staged, groups]);

  const stagedShared = useMemo(
    () => [...deltas.values()].reduce((n, d) => n + d.shared, 0),
    [deltas],
  );

  /** The views, with the staged set applied to their counts. Committed values are the server's. */
  const views: ProjectionView[] = useMemo(() => {
    if (!projection) return [];
    return projection.views.map((v) => {
      if (!v.needs) return v;
      const published = v.published + (deltas.get(v.needs)?.visible ?? 0);
      return { ...v, published, empty: v.appears && published === 0 };
    });
  }, [projection, deltas]);

  const appearing = useMemo(() => views.filter((v) => v.appears), [views]);
  const emptyViews = useMemo(() => appearing.filter((v) => v.empty), [appearing]);

  const moves: PortalMove[] = useMemo(
    () => (projection ? portalMoves(projection, { t, tp, fmtDate }) : []),
    [projection, t, tp, fmtDate],
  );

  const clientEngagements = useMemo(
    () => engagements.filter((e) => e.client_id === current?.client_id),
    [engagements, current],
  );

  /** One thread per commented object, oldest message first, newest thread on top. */
  const threads = useMemo(() => {
    const map = new Map<string, { key: string; object_type: string; object_id: number; ref: string | null; items: PortalComment[] }>();
    for (const c of [...(detail?.comments ?? [])].sort((a, b) => a.created_at.localeCompare(b.created_at))) {
      const key = `${c.object_type}:${c.object_id}`;
      const entry = map.get(key)
        ?? { key, object_type: c.object_type, object_id: c.object_id, ref: c.object_ref ?? null, items: [] };
      entry.ref = entry.ref ?? c.object_ref ?? null;
      entry.items.push(c);
      map.set(key, entry);
    }
    return [...map.values()].sort((a, b) =>
      (b.items[b.items.length - 1]?.created_at ?? '').localeCompare(a.items[a.items.length - 1]?.created_at ?? ''));
  }, [detail]);

  const awaitingKeys = useMemo(
    () => new Set((projection?.awaiting ?? []).map((a) => `${a.object_type}:${a.object_id}`)),
    [projection],
  );

  // The first thread waiting on the operator is the one that opens — that is why they came.
  useEffect(() => {
    if (openThread || !threads.length) return;
    const waiting = threads.find((t) => awaitingKeys.has(t.key));
    setOpenThread((waiting ?? threads[0]).key);
  }, [threads, awaitingKeys, openThread]);

  // §3.8 — resolve the arrival intent once the threads exist. Both `type:id` and a bare
  // object id are accepted, because notifyAllOperators passes the id alone.
  useEffect(() => {
    if (!pendingThread || !threads.length) return;
    const hit = threads.find((t) => t.key === pendingThread || String(t.object_id) === pendingThread);
    setPendingThread(null);
    if (!hit) {
      toast('warn', t('No conversation on this engagement matches “{key}”.', { key: pendingThread }));
      return;
    }
    setOpenThread(hit.key);
    // Scroll AFTER the thread has opened: opening it inserts the statement block and the reply
    // box, which move everything below — a scroll issued in the same tick lands short.
    setScrollThread(hit.key);
  }, [pendingThread, threads, toast, t]);

  useEffect(() => {
    if (!scrollThread || openThread !== scrollThread) return;
    setScrollThread(null);
    scrollToAnchor(`pa-thread-${scrollThread.replace(':', '-')}`);
  }, [scrollThread, openThread]);

  // Keep the miniature on a tab that still exists.
  useEffect(() => {
    if (!appearing.length) return;
    if (!appearing.some((v) => v.id === previewTab)) setPreviewTab(appearing[0].id);
  }, [appearing, previewTab]);

  // ── the rail ──────────────────────────────────────────────────────────────
  const rail = useMemo<PaRailCell[]>(() => {
    if (!projection) {
      return degradedRail(RAIL_KEYS, poll.error ?? t('the projection has not answered yet'))
        .map((c) => c as PaRailCell);
    }
    const p = projection;
    const problems = p.problems;
    const enabled = p.engagement.portal_enabled;

    const sharedBits = SECTIONS
      .filter((s) => s.group && (p.counts[s.type]?.published ?? 0) > 0)
      .map((s) => `${t(PORTAL_SURFACES[s.type].label.toLowerCase())} ${p.counts[s.type].published}`);

    const last = p.last_client_action;
    const wait = p.awaiting;

    const cells: PaRailCell[] = [
      {
        key: 'REACHABLE',
        // The VALUE is a count, and a figure carries no state. The condition is a state, and it
        // is the DETAIL line that turns. One loud thing per cell.
        value: hasProblem(problems, 'publication', 'counts') ? '—' : (enabled ? String(appearing.length) : t('off')),
        unit: !hasProblem(problems, 'publication', 'counts') && enabled ? t('views') : null,
        detail: hasProblem(problems, 'publication', 'counts')
          ? (problemSentence(problems, 'publication') ?? problemSentence(problems, 'counts') ?? '')
          : !enabled
            ? t('the master switch is off')
            : emptyViews.length
              ? t('{n} of them would open empty', { n: emptyViews.length })
              : cutRun(appearing.map((v) => t(v.label))) || t('nothing renders'),
        tone: hasProblem(problems, 'publication', 'counts') ? 'unknown'
          : !enabled ? 'nil' : emptyViews.length ? 'attend' : 'ink',
        route: { kind: 'in-page', anchor: 'pa-z-proj' },
      },
      {
        key: 'SHARED',
        // A real zero is INK, not an em-dash: nothing-on-record would be the em-dash, and the
        // rows are on record.
        value: hasProblem(problems, 'counts') ? '—' : String(p.shared.n),
        unit: hasProblem(problems, 'counts') ? null : t('of {total}', { total: p.shared.total }),
        detail: hasProblem(problems, 'counts')
          ? (problemSentence(problems, 'counts') ?? '')
          : staged.size
            ? t('{n} staged, not written', { n: staged.size })
            : sharedBits.length ? cutRun(sharedBits) : t('nothing published'),
        tone: hasProblem(problems, 'counts') ? 'unknown' : 'ink',
        route: { kind: 'in-page', anchor: 'pa-z-shared' },
      },
      {
        key: 'CAN SIGN IN',
        value: hasProblem(problems, 'people') ? '—' : (p.people.accounts ? String(p.people.active) : '—'),
        unit: null,
        detail: hasProblem(problems, 'people')
          ? (problemSentence(problems, 'people') ?? '')
          : p.people.accounts
            ? t('{sponsors} sponsor · {pending} invite pending', { sponsors: p.people.sponsors, pending: p.people.invite_pending })
            : t('nobody invited'),
        tone: hasProblem(problems, 'people') ? 'unknown' : p.people.active ? 'ink' : 'nil',
        route: { kind: 'in-page', anchor: 'pa-z-people' },
      },
      {
        key: 'CLIENT LAST SEEN',
        value: hasProblem(problems, 'activity') ? '—' : (last ? String(last.days) : t('never')),
        unit: !hasProblem(problems, 'activity') && last ? tCtx(t, 'd ago', 'unit') : null,
        detail: hasProblem(problems, 'activity')
          ? (problemSentence(problems, 'activity') ?? '')
          : last
            ? `${firstName(last.who)} · ${last.action}`
            : t('no portal action on record'),
        tone: hasProblem(problems, 'activity') ? 'unknown' : last ? 'ink' : 'nil',
        route: { kind: 'in-page', anchor: 'pa-z-people' },
      },
      {
        key: 'AWAITING YOU',
        value: hasProblem(problems, 'comments') ? '—' : (wait.length ? String(wait.length) : '—'),
        unit: !hasProblem(problems, 'comments') && wait.length ? (wait.length === 1 ? t('comment') : t('comments')) : null,
        detail: hasProblem(problems, 'comments')
          ? (problemSentence(problems, 'comments') ?? '')
          : wait.length
            ? `${wait[0].ref ?? wait[0].object_type} · ${tp(wait[0].days, '{n} day', '{n} days')}`
            : t('nothing unanswered'),
        tone: hasProblem(problems, 'comments') ? 'unknown' : wait.length ? 'attend' : 'nil',
        route: { kind: 'in-page', anchor: 'pa-z-talk' },
      },
    ];
    return cells;
  }, [projection, poll.error, appearing, emptyViews, staged, t, tp]);

  const RAIL_GO: Record<PaRailKey, { label: string; anchor: string }> = {
    REACHABLE: { label: 'the projection', anchor: 'pa-z-proj' },
    SHARED: { label: 'what is shared', anchor: 'pa-z-shared' },
    'CAN SIGN IN': { label: 'people', anchor: 'pa-z-people' },
    'CLIENT LAST SEEN': { label: 'people', anchor: 'pa-z-people' },
    'AWAITING YOU': { label: 'the thread', anchor: 'pa-z-talk' },
  };

  // ── mutations ─────────────────────────────────────────────────────────────

  const setPortalEnabled = async (enabled: boolean) => {
    if (!engagementId) return;
    setBusy(true);
    try {
      await portalAdminApi.setPortalEnabled(engagementId, enabled);
      toast('ok', enabled
        ? t('The portal is on — grants and publication settings now take effect')
        : t('The portal is off — clients lose access immediately'));
      await nav.reload();
      await refreshAll();
    } catch (e) { err(e, t('Could not change the portal switch')); } finally { setBusy(false); setConfirmOff(false); }
  };

  const setVisibility = async (type: PortalObjectType, visibility: Visibility) => {
    if (!engagementId) return;
    try {
      await portalAdminApi.savePublication(engagementId, { [type]: visibility });
      await poll.refresh();
    } catch (e) { err(e, t('Could not save the publication setting')); }
  };

  const toggleRow = (group: string, row: PublishableRow) => {
    const k = `${group}:${row.id}`;
    setStaged((prev) => {
      const next = new Map(prev);
      // Staging back to the committed value is not a change — it is a cancellation.
      if (next.has(k)) next.delete(k); else next.set(k, !row.published);
      return next;
    });
  };

  /**
   * "Share every HELD row in scope". The old verb was "Publish all in scope" and it filtered
   * `excluded` while never consulting `status` — so a draft rode along. `status !== 'draft'` is
   * the whole fix, and it has a contractual consequence.
   */
  const bulk = (group: string, direction: 'on' | 'off') => {
    const rows = rowsOf(group);
    setStaged((prev) => {
      const next = new Map(prev);
      for (const row of rows) {
        const k = `${group}:${row.id}`;
        if (direction === 'on') {
          if (row.excluded || row.status === 'draft' || row.status === 'proposed') continue;
          if (!row.published) next.set(k, true); else next.delete(k);
        } else if (row.published) next.set(k, false); else next.delete(k);
      }
      return next;
    });
  };

  /** One POST per (table, published) pair — the shape the publish endpoint already has. */
  const commitStaged = async () => {
    if (!groups || !staged.size) return;
    setBusy(true);
    const batches = new Map<string, { table: string; ids: number[]; published: boolean }>();
    for (const [key, want] of staged) {
      const [group, rawId] = key.split(':');
      const g = groups[group];
      if (!g) continue;
      const bk = `${group}:${want}`;
      const batch = batches.get(bk) ?? { table: g.table, ids: [], published: want };
      batch.ids.push(Number(rawId));
      batches.set(bk, batch);
    }
    let done = 0;
    const refused: string[] = [];
    for (const batch of batches.values()) {
      try {
        await portalAdminApi.publish(batch.table, batch.ids, batch.published,
          batch.table === 'documents' ? engagementId ?? undefined : undefined);
        done += batch.ids.length;
      } catch (e) {
        refused.push(e instanceof Error ? e.message : t('{table} was refused', { table: batch.table }));
      }
    }
    // A failed batch leaves the staged set INTACT so it can be retried — which the old
    // write-on-toggle model could not offer.
    if (!refused.length) setStaged(new Map());
    setConfirmStage(false);
    setBusy(false);
    if (refused.length) toast('err', `${tp(done, '{n} row written', '{n} rows written')} · ${refused.join(' · ')}`);
    else toast('ok', tp(done, '{n} row written', '{n} rows written'));
    await refreshAll();
  };

  const stageSummary = useMemo(() => {
    let on = 0; let off = 0; let drafts = 0; let exclusions = 0;
    if (groups) {
      for (const [key, want] of staged) {
        const [group, rawId] = key.split(':');
        const row = groups[group]?.rows.find((r) => r.id === Number(rawId));
        if (want) {
          on += 1;
          if (row?.status === 'draft' || row?.status === 'proposed') drafts += 1;
          if (row?.excluded) exclusions += 1;
        } else off += 1;
      }
    }
    return { on, off, drafts, exclusions };
  }, [staged, groups]);

  const sendReply = async (objectType: string, objectId: number) => {
    if (!engagementId) return;
    const key = `${objectType}:${objectId}`;
    const body = (replyDraft[key] ?? '').trim();
    if (!body) return;
    setReplyBusy(key);
    try {
      await portalAdminApi.reply({ engagement_id: engagementId, object_type: objectType, object_id: objectId, body });
      setReplyDraft((d) => ({ ...d, [key]: '' }));
      toast('ok', t('Reply posted — the client sees it on the object'));
      await refreshAll();
    } catch (e) { err(e, t('Could not post the reply')); } finally { setReplyBusy(null); }
  };

  const createUser = async () => {
    if (!engagementId || !newFrom) return;
    setBusy(true);
    try {
      const user = await portalAdminApi.createUser({
        engagement_id: engagementId,
        email: newFrom.email.trim(),
        name: newFrom.name.trim(),
        role: newFrom.role,
        contact_id: newFrom.contact?.id ?? null,
      });
      toast('ok', t('{name} created and granted {code}', { name: user.name, code: current?.code }));
      if (user.warning) toast('warn', user.warning);
      setNewFrom(null);
      await refreshAll();
    } catch (e) { err(e, t('Could not create the portal user')); } finally { setBusy(false); }
  };

  const issueInvite = async (user: PortalUser) => {
    try {
      const result = await portalAdminApi.invite(user.id, engagementId ?? undefined);
      setInvites((prev) => ({ ...prev, [user.id]: result }));
      if (result.warning) toast('warn', result.warning);
      else toast('ok', t('Invite issued for {name} — the link is on their row', { name: result.name }));
      await refreshAll();
    } catch (e) { err(e, t('Could not issue the invite')); }
  };

  const revokeInvite = async (user: PortalUser) => {
    try {
      await portalAdminApi.revokeInvite(user.id);
      setInvites((prev) => { const next = { ...prev }; delete next[user.id]; return next; });
      toast('ok', t('The outstanding invite for {name} is dead', { name: user.name }));
      await refreshAll();
    } catch (e) { err(e, t('Could not revoke the invite')); }
  };

  const copyInvite = async (result: InviteResult) => {
    try {
      await navigator.clipboard.writeText(result.invite_url);
      toast('ok', t('Invite link copied'));
    } catch {
      toast('warn', t('Clipboard blocked — select the link and copy it manually'));
    }
  };

  const toggleGrant = async (user: PortalUser, engId: number, granted: boolean) => {
    try {
      if (granted) {
        const out = await portalAdminApi.addGrant(user.id, engId);
        if (out.warning) toast('warn', out.warning);
      } else {
        await portalAdminApi.removeGrant(user.id, engId);
      }
      await refreshAll();
    } catch (e) { err(e, t('Could not change the grant')); }
  };

  const toggleDisabled = async (user: PortalUser) => {
    try {
      await portalAdminApi.updateUser(user.id, { disabled: !user.disabled, engagement_id: engagementId ?? undefined });
      toast('ok', user.disabled ? t('{name} re-enabled', { name: user.name }) : t('{name} disabled', { name: user.name }));
      await refreshAll();
    } catch (e) { err(e, t('Could not update the user')); }
  };

  const changeRole = async (user: PortalUser, role: PortalRole) => {
    try {
      await portalAdminApi.updateUser(user.id, { role, engagement_id: engagementId ?? undefined });
      await refreshAll();
    } catch (e) { err(e, t('Could not change the role')); }
  };

  /** Fetch the plan first — the dialog must be able to answer "why not", not just refuse. */
  const askRemoveUser = async (user: PortalUser) => {
    try {
      setDeleteTarget({ user, plan: await portalAdminApi.userDeletionPlan(user.id) });
    } catch (e) { err(e, t('Could not work out what removing this account would do')); }
  };

  const removeUser = async () => {
    if (!deleteTarget) return;
    const { user, plan } = deleteTarget;
    setRemoving(true);
    try {
      if (!plan.allowed) {
        if (!user.disabled) {
          await portalAdminApi.updateUser(user.id, { disabled: true, engagement_id: engagementId ?? undefined });
          toast('ok', t('{name} disabled — access is gone, the record stands', { name: user.name }));
        }
      } else {
        await portalAdminApi.deleteUser(user.id, engagementId ?? undefined);
        toast('ok', t('{name} removed', { name: user.name }));
      }
      setDeleteTarget(null);
      await refreshAll();
    } catch (e) { err(e, t('Could not remove the user')); setDeleteTarget(null); } finally { setRemoving(false); }
  };

  const openPreview = async () => {
    if (!engagementId) return;
    try {
      const ctx = await portalAdminApi.previewContext(engagementId);
      window.open(ctx.preview_path, '_blank', 'noopener');
      toast('ok', ctx.notice);
    } catch (e) { err(e, t('Could not create a preview link')); }
  };

  const goAnchor = (anchor: string) => scrollToAnchor(anchor);

  // ── no engagement ─────────────────────────────────────────────────────────
  if (!current) {
    return (
      <div className="page pa">
        <header className="mast"><div className="mast-top"><div className="mast-main"><h1>{t('Client portal')}</h1></div></div></header>
        <EmptyState
          kind="empty"
          title={t('No engagement selected')}
          detail={t('Portal access and publication are decided per engagement — the master switch, the object-type matrix and every publish flag hang off one engagement id.')}
        />
      </div>
    );
  }

  // ── the projection never answered ─────────────────────────────────────────
  if (!projection) {
    return (
      <div className="page pa">
        <header className="mast">
          <div className="mast-top"><div className="mast-main">
            <h1>{t('Client portal')}</h1>
            <div className="mast-sub"><Ref>{current.code}</Ref> {current.name}</div>
          </div></div>
        </header>
        {poll.loading
          ? <div className="sheet sheet--clip pa-sheet"><p className="pa-pad zone-empty">{t('Resolving what the client would find…')}</p></div>
          : (
            <EmptyState
              kind="error"
              title={t('The projection could not be computed')}
              detail={poll.error ?? t('GET /api/portal-admin/projection did not answer.')}
              onRetry={() => void poll.refresh()}
            />
          )}
      </div>
    );
  }

  const enabled = projection.engagement.portal_enabled;
  const lead = moves[0] ?? null;
  const rest = moves.slice(1);

  return (
    <div className="page pa">
      {/* ══ masthead ══ */}
      <header className="mast">
        <div className="mast-top">
        <div className="mast-main">
          <h1 className="pa-h1">
            {t('Client portal')}
            <span className={`pa-dot${enabled ? '' : ' pa-dot--off'}`} />
            <span className="pa-state">{enabled ? t('portal on') : t('portal off')}</span>
          </h1>
          <div className="mast-sub">
            <Ref>{projection.engagement.code}</Ref> {projection.engagement.name}
            <span className="sep">·</span> {projection.engagement.client_name}
            <span className="sep">·</span> <span>{t(projection.engagement.phase)}</span>
            {poll.notice && <><span className="sep">·</span> <span>{poll.notice}</span></>}
          </div>
        </div>
        </div>
      </header>

      {/* ══ the rail — five cells, fixed set, fixed order, every one engagement-scoped ══ */}
      <div className="instr pa-instr">
        <div className="rail">
          {rail.map((cell) => (
            <button
              key={cell.key}
              type="button"
              className="fld"
              title={t('Open {what}', { what: t(RAIL_GO[cell.key].label) })}
              onClick={() => goAnchor(RAIL_GO[cell.key].anchor)}
            >
              <div className="fl u-label">
                {t(cell.key)}
                <span className="go" aria-hidden="true">{t(RAIL_GO[cell.key].label)}</span>
              </div>
              <div className={`fv voice-figure${TONE_CLASS[cell.tone] ?? ''}`}>
                {cell.tone === 'unknown'
                  ? <span className="n-unknown" title={cell.detail}>—</span>
                  : <>
                    {cell.value}
                    {cell.unit && <span className="u">{cell.unit}</span>}
                    {cell.key === 'SHARED' && stagedShared !== 0 && (
                      <span className="pa-move">→ {projection.shared.n + stagedShared}</span>
                    )}
                  </>}
              </div>
              <div className="fd">
                {cell.key === 'SHARED' && staged.size
                  ? <span className="act">{cell.detail}</span>
                  : cell.tone === 'attend' ? <span className="warn">{cell.detail}</span> : cell.detail}
              </div>
            </button>
          ))}
        </div>
        {/* THE MASTER SWITCH, on the rail's own sheet. It is the one boolean the whole page
            hangs off, and a committing control never sits on the bare field. */}
        <div className="instr-band pa-switch">
          <span className="pa-switch-say">
            {enabled
              ? <>{t('The portal is')} <b>{tCtx(t, 'on', 'state')}</b> {t('for this engagement. Publication decides the rest.')}</>
              : <>{t('The portal is')} <b>{t('off')}</b>. {t('Every portal call is refused before publication is even consulted — grants and publish flags are kept, and take effect the moment it flips.')}</>}
          </span>
          <span className="pa-switch-acts">
            {enabled && (
              <button type="button" className="btn btn-sm" onClick={() => goAnchor('pa-z-proj')}>
                {t('What they see')}
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy}
              onClick={() => (enabled ? setConfirmOff(true) : void setPortalEnabled(true))}
            >
              {enabled ? t('Switch the portal off') : t('Switch the portal on')}
            </button>
          </span>
        </div>
      </div>

      {/* ══ zone 1 · next move ══ */}
      <section className="zone" id="pa-z-move">
        <div className="zone-head">
          <span className="u-label">{t('Next move')}</span>
          <span className="zone-count voice-figure">{moves.length ? t('{n} open', { n: moves.length }) : ''}</span>
        </div>
        {!lead ? (
          <div className="sheet sheet--clip pa-sheet"><p className="pa-pad zone-empty">{t(ALL_CLEAR)}</p></div>
        ) : (
          <>
            <div className="sheet sheet--edge pa-move">
              <div className="pa-move-head">
                <h2>{lead.title}</h2>
                <div className="pa-move-cta">
                  <button
                    type="button"
                    className="btn btn-accent btn-sm"
                    disabled={busy}
                    onClick={() => (lead.act === 'enable-portal' ? void setPortalEnabled(true) : goAnchor(lead.anchor))}
                  >
                    {lead.cta}
                  </button>
                </div>
              </div>
              <p className="read pa-move-why">{lead.why}</p>
              <div className="pa-chips">
                {(lead.refs ?? []).map((r) => <span key={r} className="pa-chip src-ref">{r}</span>)}
              </div>
              {rest.length > 0 && (
                <button type="button" className="pa-qtoggle" onClick={() => setShowRest((v) => !v)}>
                  <span>{showRest ? '⌄' : '›'}</span> {t('{n} more, ranked', { n: rest.length })}
                </button>
              )}
            </div>
            {rest.length > 0 && (
              <>
                {showRest && (
                  <div className="pa-qlist">
                    {rest.map((m, i) => (
                      <div className="pa-qrow" key={m.rule}>
                        <span className="pa-qn voice-figure">{i + 2}</span>
                        <div>
                          <div className="pa-qtitle">{m.title}</div>
                          <div className="pa-qwhy">{m.why}</div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{ marginLeft: 'auto', flex: 'none' }}
                          onClick={() => (m.act === 'enable-portal' ? void setPortalEnabled(true) : goAnchor(m.anchor))}
                        >
                          {m.cta}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </section>

      {/* ══ zone 2 · what the client would find ══ */}
      <section className="zone" id="pa-z-proj">
        <div className="zone-head">
          <span className="u-label">{t('What the client would find')}</span>
          <span className="zone-count voice-figure">
            {enabled
              ? t('{n} of {total} views render', { n: appearing.length, total: projection.views.length })
              : t('the portal is off — nothing renders')}
          </span>
          <button type="button" className="zone-route" onClick={() => void openPreview()}>
            {t('Open the full preview →')}
          </button>
        </div>
        {/* F5.1 — a source that failed says so in its own words, in the zone that lost it. */}
        <ProblemState
          problems={projection.problems} source="publication"
          zone={t('The visibility matrix')} onRetry={() => void poll.refresh()}
        />
        <div className="pa-proj">
          <div>
            <div className="sheet sheet--clip pa-sheet">
              {!enabled ? (
                <div className="pa-pad">
                  <p className="zone-empty">
                    {t('Nothing renders. With the master switch off every portal call is refused before publication is even consulted — a client who follows an invite gets a sign-in page and')} <em>{t('“this engagement is not shared with you”')}</em>.
                  </p>
                  <div className="pa-chips">
                    {projection.views.map((v) => (
                      <span key={v.id} className="pa-chip" style={{ borderStyle: 'solid' }}>{t(v.label)}</span>
                    ))}
                  </div>
                  <p className="read" style={{ marginTop: 'var(--s12)', fontSize: 'var(--t-scan)' }}>
                    {tp(projection.views.length,
                      '{n} view suppressed by one boolean. Their publication settings are kept and take effect the moment it flips.',
                      '{n} views suppressed by one boolean. Their publication settings are kept and take effect the moment it flips.')}
                  </p>
                </div>
              ) : (
                projection.views.map((v) => {
                  const shown = views.find((x) => x.id === v.id) ?? v;
                  return (
                    <div className={`pa-navtab${shown.appears ? '' : ' pa-navtab--off'}`} key={v.id}>
                      <span className="pa-navtab-mark">
                        {shown.appears
                          ? <span className={`att ${shown.empty ? 'att--proposed' : 'att--held'}`} />
                          : <span className="n-none">—</span>}
                      </span>
                      <div>
                        <div className="pa-navtab-name">{t(v.label)}</div>
                        <div className="pa-navtab-why">
                          {!shown.appears ? (
                            <><Ref>{v.needs}</Ref> {t('is hidden — the tab is not in their navigation')}</>
                          ) : shown.empty ? (
                            <><span className="warn">{t('renders, and finds nothing')}</span> — {t('0 of')} <Fig>{shown.total}</Fig> {t('published')}</>
                          ) : v.needs ? (
                            <><Fig>{shown.published}</Fig> {t('of')} <Fig>{shown.total}</Fig> {t('published')}</>
                          ) : t('always present')}
                        </div>
                      </div>
                      <div className="pa-navtab-cap">{shown.appears ? t(shown.capability) : ''}</div>
                    </div>
                  );
                })
              )}
              {enabled && (
                <p className="pa-projnote">
                  {t('Resolved from the same three gates the portal itself applies — engagement switch, object-type visibility, row flag. Nothing here is a screenshot and nothing here is a guess.')}
                </p>
              )}
            </div>
          </div>

          {/* the miniature — DAY-PINNED (LAW 4: the portal has no theme toggle) */}
          <div className="pa-preview">
            <div className="pv-head">
              <div className="pv-brand">{t('Signet portal')}</div>
              <div className="pv-eng">{projection.engagement.name} — {projection.engagement.client_name}</div>
            </div>
            <div className="pv-tabs">
              {enabled && appearing.length
                ? appearing.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    className={`pv-tab${v.id === previewTab ? ' on' : ''}`}
                    onClick={() => setPreviewTab(v.id)}
                  >
                    {t(v.label)}
                  </button>
                ))
                : <span className="pv-tab">{t('no tabs')}</span>}
            </div>
            <div className="pv-body">
              <PreviewBody
                enabled={enabled}
                view={appearing.find((v) => v.id === previewTab) ?? appearing[0] ?? null}
                appearing={appearing}
                rowsFor={(type) => {
                  const section = SECTIONS.find((s) => s.type === type);
                  if (!section?.group) return { rows: [], voice: 'label' as Voice };
                  return {
                    rows: rowsOf(section.group).filter((r) => isPub(section.group!, r) && countsInPortal(type, r)),
                    voice: section.voice,
                  };
                }}
              />
            </div>
            <div className="pv-note">
              {staged.size
                ? tp(staged.size, 'Including {n} staged change — nothing is written yet.', 'Including {n} staged changes — nothing is written yet.')
                : t('Preview pins day paper — the portal has no theme toggle.')}
            </div>
          </div>
        </div>
      </section>

      {/* ══ zone 3 · what is shared ══ */}
      <section className="zone" id="pa-z-shared">
        <div className="zone-head">
          <span className="u-label">{t('What is shared')}</span>
          <span className="zone-count voice-figure">
            {t('{n} of {total} rows', { n: projection.shared.n, total: projection.shared.total })} ·{' '}
            {t('{n} of {total} types visible', { n: SECTIONS.filter((s) => projection.matrix[s.type] !== 'hidden').length, total: SECTIONS.length })}
            {staged.size ? <span className="act"> · {t('{n} staged', { n: staged.size })}</span> : null}
          </span>
        </div>

        <ProblemState
          problems={projection.problems} source="counts"
          zone={t('The shared tallies')} onRetry={() => void poll.refresh()}
        />
        {detailError && !detail ? (
          <EmptyState
            kind="error"
            title={t('The publish lists could not be read')}
            detail={detailError}
            onRetry={() => void loadDetail()}
          />
        ) : !detail ? (
          <div className="sheet sheet--clip pa-sheet">
            <p className="pa-pad zone-empty">{detailLoading ? t('Reading the register…') : t('Nothing loaded.')}</p>
          </div>
        ) : (
          <div className="sheet sheet--clip pa-sheet">
            {SECTIONS.map((section) => {
              const surface = PORTAL_SURFACES[section.type];
              const vis = projection.matrix[section.type] ?? 'hidden';
              const counts = projection.counts[section.type];
              const rows = rowsOf(section.group);
              const isOpen = open.has(section.type);
              const pubNow = counts.published + (deltas.get(section.type)?.shared ?? 0);
              const draftsShared = rows.filter((r) => section.group && isPub(section.group, r)
                && (r.status === 'draft' || r.status === 'proposed')).length;

              const notes: React.ReactNode[] = [];
              if (!section.group) notes.push(<span key="flag">{t('no per-row flag')}</span>);
              if (draftsShared) notes.push(<span key="d" className="warn">{tp(draftsShared, '{n} draft shared', '{n} drafts shared')}</span>);
              if (counts.excluded) notes.push(<span key="x"><Fig>{counts.excluded}</Fig> {t('excluded')}</span>);
              if (vis !== 'hidden' && counts.total === 0 && section.group) {
                notes.push(<span key="e" className="warn">{t('visible, nothing exists')}</span>);
              }
              if (vis !== 'hidden' && surface.view === null && section.group) {
                notes.push(<span key="v" className="warn">{t('no client view lists these')}</span>);
              }

              return (
                <div
                  className={`pa-tsec${isOpen ? ' pa-tsec--open' : ''}${vis === 'hidden' ? ' pa-tsec--hidden' : ''}`}
                  key={section.type}
                >
                  <button
                    type="button"
                    className="pa-tsec-head"
                    onClick={() => setOpen((prev) => {
                      const next = new Set(prev);
                      if (next.has(section.type)) next.delete(section.type); else next.add(section.type);
                      return next;
                    })}
                  >
                    <span className="pa-tsec-caret">›</span>
                    <span className="pa-tsec-name">{t(surface.label)}</span>
                    {/* The decision lives ADJACENT to what it governs — the 30-input matrix is gone. */}
                    <span onClick={(e) => e.stopPropagation()}>
                      <select
                        className="form-select"
                        style={{ fontSize: 'var(--t-meta)', padding: '3px 6px' }}
                        aria-label={t('{label} visibility', { label: t(surface.label) })}
                        value={vis}
                        onChange={(e) => void setVisibility(section.type, e.target.value as Visibility)}
                      >
                        {VISIBILITIES.map((v) => <option key={v} value={v}>{t(VIS_LABEL[v])}</option>)}
                      </select>
                    </span>
                    <span className="pa-tsec-tally">
                      {!section.group ? (
                        vis === 'hidden'
                          ? t('not in the portal')
                          : t('the type gate is the whole decision')
                      ) : vis === 'hidden' ? (
                        <span className="n-none">
                          {tp(counts.total, '{n} row on record, none reachable', '{n} rows on record, none reachable')}
                        </span>
                      ) : (
                        <>
                          <span className="strong voice-figure">{pubNow}</span> {t('of')}{' '}
                          <Fig>{counts.in_scope}</Fig> {t('in scope shared')}
                          {vis === 'read_act' && surface.act ? <> · {t('they can {act}', { act: t(surface.act) })}</> : null}
                        </>
                      )}
                    </span>
                    <span className="pa-tsec-note">
                      {notes.length ? notes.map((n, i) => (
                        <span key={i}>{i > 0 && <span className="sep"> · </span>}{n}</span>
                      )) : null}
                    </span>
                  </button>

                  {isOpen && (
                    <div className="pa-tsec-body">
                      {!section.group ? (
                        <p className="read" style={{ fontSize: 'var(--t-scan)' }}>
                          {t('Defects carry no')} <Ref>published</Ref> {t('column. The client raised them, so the type gate is the only decision there is.')}
                        </p>
                      ) : rows.length === 0 ? (
                        <p className="read" style={{ fontSize: 'var(--t-scan)' }}>
                          {t('Nothing of this kind exists in')} <Ref>{projection.engagement.code}</Ref> {t('yet.')}
                          {vis !== 'hidden' && ` ${t('The tab would still render for the client, and it would be empty.')}`}
                        </p>
                      ) : (
                        <>
                          <div className="pa-tsec-verbs">
                            <button type="button" className="btn btn-sm" onClick={() => bulk(section.group!, 'on')}>
                              {t('Share every held row in scope')}
                            </button>
                            <button type="button" className="btn btn-sm btn-ghost" onClick={() => bulk(section.group!, 'off')}>
                              {t('Share none')}
                            </button>
                            <span className="spacer">
                              {t('showing')} <Fig>{Math.min(rows.length, expanded.has(section.type) ? rows.length : ROW_WINDOW)}</Fig>{' '}
                              {t('of')} <Fig>{rows.length}</Fig>
                            </span>
                          </div>
                          {(expanded.has(section.type) ? rows : rows.slice(0, ROW_WINDOW)).map((row) => {
                            const on = isPub(section.group!, row);
                            const isStaged = staged.has(`${section.group}:${row.id}`);
                            const cls = isStaged ? (on ? 'pa-pub--staged' : 'pa-pub--stagedoff') : (on ? 'pa-pub--on' : '');
                            const mark = markOf(section.voice, row);
                            const drifted = projection.drift.find((d) => d.ref === row.label);
                            return (
                              <div className={`pa-prow${on ? '' : ' pa-prow--off'}`} key={row.id}>
                                <span className={`att ${mark ?? ''}`} />
                                <span className="pa-prow-ref">{row.label}</span>
                                <div>
                                  <div className={`pa-prow-stmt pa-prow-stmt--${section.voice}`}>
                                    {row.detail || <span className="n-none">—</span>}
                                  </div>
                                  <div className="pa-prow-meta">
                                    <span>{t(REQUIREMENT_STATUS_LABEL[row.status]?.word ?? row.status)}</span>
                                    {row.excluded && <span className="warn">{t('exclusion')}</span>}
                                    {on && (row.status === 'draft' || row.status === 'proposed')
                                      && <span className="warn">{t('draft, and shared')}</span>}
                                    {on && drifted && (
                                      <span className="warn">
                                        {t('revised {date}, after the publish pass', { date: fmtDate(drifted.revised_at) })}
                                      </span>
                                    )}
                                    {on && !countsInPortal(section.type, row) && (
                                      <span className="warn">
                                        {t('flagged, and the portal still will not list it — it is {status}', { status: t(REQUIREMENT_STATUS_LABEL[row.status]?.word ?? row.status) })}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className={`pa-pub ${cls}`}
                                  onClick={() => toggleRow(section.group!, row)}
                                >
                                  <span className="pa-pub-dot" />
                                  {isStaged ? (on ? t('to share') : t('to hide')) : (on ? t('shared') : t('not shared'))}
                                </button>
                              </div>
                            );
                          })}
                          {rows.length > ROW_WINDOW && !expanded.has(section.type) && (
                            <div style={{ padding: '10px 0 0' }}>
                              <button
                                type="button"
                                className="btn btn-sm btn-ghost"
                                onClick={() => setExpanded((prev) => new Set(prev).add(section.type))}
                              >
                                {t('Show the remaining')} <Fig>{rows.length - ROW_WINDOW}</Fig> →
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ══ zone 4 · who can sign in ══ */}
      <section className="zone" id="pa-z-people">
        <div className="zone-head">
          <span className="u-label">{t('Who can sign in')}</span>
          <span className="zone-count voice-figure">
            {projection.people.accounts ? tp(projection.people.accounts, '{n} account', '{n} accounts') : ''}
          </span>
        </div>
        <div className="sheet sheet--clip pa-sheet">
          {!detail ? (
            <p className="pa-pad zone-empty">{detailLoading ? t('Reading the accounts…') : (detailError ?? t('Nothing loaded.'))}</p>
          ) : detail.users.length === 0 ? (
            <>
              <p className="pa-pad zone-empty">
                {t('Nobody at {client} can sign in.', { client: projection.engagement.client_name })}
                {detail.contacts.length
                  ? ` ${tp(detail.contacts.length,
                    '{n} contact is already on the client record — a portal account can be made from one without retyping it.',
                    '{n} contacts are already on the client record — a portal account can be made from one without retyping it.')}`
                  : ` ${t('No contact is on the client record either, so there is nobody to make an account from yet.')}`}
              </p>
              {detail.contacts.map((c) => (
                <div className="pa-prow2" key={c.id}>
                  <div>
                    <div className="pa-pname">{c.name}</div>
                    <div className="pa-pmail">{c.email ?? <span className="n-none">{t('no email on record')}</span>}</div>
                  </div>
                  <div className="pa-pstate">
                    {c.is_sponsor && <span className="badge badge-plain">{t('sponsor on the record')}</span>}
                  </div>
                  <div>
                    <div className="pa-pcan">{c.role_title ?? <span className="none">{t('no role on record')}</span>}</div>
                    <div className="pa-pstate" style={{ marginTop: 4 }}>
                      <span className="n-none">{t('no portal account')}</span>
                    </div>
                  </div>
                  <div className="pa-pverbs">
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={!c.email}
                      title={c.email ? undefined : t('This contact has no email address on record')}
                      onClick={() => setNewFrom({
                        contact: c,
                        name: c.name,
                        email: c.email ?? '',
                        role: c.is_sponsor ? 'sponsor' : 'reviewer',
                      })}
                    >
                      {c.is_sponsor ? t('Invite as sponsor') : t('Invite as reviewer')}
                    </button>
                  </div>
                </div>
              ))}
              <div className="pa-pad" style={{ borderTop: '1px solid var(--rule)' }}>
                {!enabled && (
                  <p className="read" style={{ color: 'var(--attend)', fontSize: 'var(--t-scan)', marginBottom: 'var(--s8)' }}>
                    {t('The portal is off, so an invite issued now cannot be accepted. Switch it on in the masthead, or the link lands on a locked door.')}
                  </p>
                )}
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setNewFrom({ contact: null, name: '', email: '', role: 'reviewer' })}
                >
                  {t('Create an account by hand')}
                </button>
              </div>
            </>
          ) : (
            <>
              {detail.users.map((u) => {
                const invite = invites[u.id];
                return (
                  <div className="pa-prow2" key={u.id}>
                    <div>
                      <div className="pa-pname">{u.name}</div>
                      <div className="pa-pmail">{u.email}</div>
                      {u.contact_name && <div className="pa-plink">{t('linked to the contact record')}</div>}
                    </div>
                    <div>
                      <div className="pa-pstate">
                        {u.disabled ? <span className="badge badge-err">{t('disabled')}</span>
                          : u.activated ? <span className="badge badge-ok">{t('active')}</span>
                            : u.invite_expired ? <span className="badge badge-err">{t('invite expired')}</span>
                              : u.invite_pending ? <span className="badge badge-warn">{t('invited')}</span>
                                : <span className="badge badge-plain">{t('no invite')}</span>}
                      </div>
                      <div className="pa-pstate" style={{ marginTop: 4 }}>
                        <select
                          className="form-select"
                          style={{ fontSize: 'var(--t-meta)', padding: '2px 5px' }}
                          aria-label={t('Role for {name}', { name: u.name })}
                          value={u.role}
                          onChange={(e) => void changeRole(u, e.target.value as PortalRole)}
                        >
                          <option value="sponsor">{t('sponsor')}</option>
                          <option value="reviewer">{t('reviewer')}</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <div className="pa-pcan">{capabilityOf(u, views, projection.matrix, t)}</div>
                      <div className="pa-pstate" style={{ marginTop: 4 }}>
                        {u.last_seen_at
                          ? <>{t('last seen')} <Fig>{fmtDateTime(u.last_seen_at)}</Fig> · <Fig>{u.audit_events}</Fig>{' '}
                            {u.audit_events === 1 ? t('portal action') : t('portal actions')}</>
                          : u.invite_pending && u.invite_expires_at
                            ? <>{t('link expires')} <Fig>{fmtDate(String(u.invite_expires_at))}</Fig></>
                            : u.last_login_at ? t('signed in, nothing done yet') : t('never signed in')}
                      </div>
                      {u.grants.length > 1 && (
                        <div className="pa-plink">
                          {t('granted {codes}', { codes: u.grants.map((g) => g.code).join(', ') })}
                        </div>
                      )}
                      {invite && (
                        <div className="pa-invite">
                          <input readOnly value={invite.invite_url} onFocus={(e) => e.currentTarget.select()} />
                          <button type="button" className="btn btn-sm" onClick={() => void copyInvite(invite)}>{t('Copy link')}</button>
                        </div>
                      )}
                    </div>
                    <div className="pa-pverbs">
                      <button type="button" className="btn btn-sm" onClick={() => void issueInvite(u)}>
                        {u.activated ? t('Re-invite') : t('Invite')}
                      </button>
                      {u.invite_pending && (
                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => void revokeInvite(u)}>
                          {t('Revoke')}
                        </button>
                      )}
                      {clientEngagements.length > 1 && (
                        <button type="button" className="btn btn-sm" onClick={() => setGrantUser(u)}>{t('Grants')}</button>
                      )}
                      <button type="button" className="btn btn-sm" onClick={() => void toggleDisabled(u)}>
                        {u.disabled ? t('Re-enable') : t('Disable')}
                      </button>
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => void askRemoveUser(u)}>
                        {t('Delete')}
                      </button>
                    </div>
                  </div>
                );
              })}
              <div className="pa-pad" style={{ borderTop: '1px solid var(--rule)' }}>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setNewFrom({ contact: null, name: '', email: '', role: 'reviewer' })}
                >
                  {t('New portal user')}
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* ══ zone 5 · conversation ══ */}
      <section className="zone" id="pa-z-talk">
        <div className="zone-head">
          <span className="u-label">{t('Conversation')}</span>
          {/* B8: "0 awaiting you" is a CLAIM. If the source that counts them did not answer,
              the zone says so instead of printing a zero it cannot stand behind. */}
          <span className="zone-count voice-figure">
            {hasProblem(projection.problems, 'comments')
              ? <span className="n-unknown">{t('awaiting-you not counted')}</span>
              : threads.length ? t('{n} awaiting you of {total}', { n: projection.awaiting.length, total: threads.length }) : ''}
          </span>
        </div>
        <ProblemState
          problems={projection.problems} source="comments"
          zone={t('The awaiting-you list')} onRetry={() => void poll.refresh()}
        />
        <div className="sheet sheet--clip pa-sheet">
          {!detail ? (
            <p className="pa-pad zone-empty">{detailLoading ? t('Reading the threads…') : (detailError ?? t('Nothing loaded.'))}</p>
          ) : threads.length === 0 ? (
            <p className="pa-pad zone-empty">
              {t('Nothing has been said. A client comment lands here with the statement it is about, so the answer does not need a second tab.')}
            </p>
          ) : (
            threads.map((th) => {
              // A thread is only "answered" when the source that decides that answered too.
              const awaitingKnown = !hasProblem(projection.problems, 'comments');
              const awaiting = awaitingKeys.has(th.key);
              const isOpen = openThread === th.key;
              const last = th.items[th.items.length - 1];
              // D11 — the evidence block. The comment's ref is already resolved server-side; the
              // statement it points at is one lookup into the list this page already loaded.
              const subject = th.ref
                ? Object.values(detail.publishable.objects)
                  .flatMap((g) => g.rows.map((r) => ({ g, r })))
                  .find(({ r }) => r.label === th.ref)
                : undefined;
              return (
                <div
                  className={`pa-thread${isOpen ? ' pa-thread--lit' : ''}`}
                  key={th.key}
                  id={`pa-thread-${th.key.replace(':', '-')}`}
                >
                  <div className="pa-thread-head">
                    <button
                      type="button"
                      className="pa-thread-ref"
                      onClick={() => void openRoute(ROUTES.register({
                        engagement: engagementId ?? undefined,
                        ref: th.ref ?? undefined,
                      }))}
                      title={th.ref ? t('Open {ref} in the Register', { ref: th.ref }) : t('Open the Register')}
                    >
                      {th.ref ?? `${th.object_type.replace(/_/g, ' ')} #${th.object_id}`} →
                    </button>
                    {!awaitingKnown
                      ? <span className="badge badge-plain n-unknown">{t('state unknown')}</span>
                      : awaiting
                        ? <span className="badge badge-warn">{t('awaiting your reply')}</span>
                        : <span className="badge badge-ok">{t('answered')}</span>}
                    <span className="pa-thread-age">
                      {tp(daysSince(last?.created_at ?? null), '{n} day', '{n} days')}
                    </span>
                    {!isOpen && (
                      <button
                        type="button"
                        className="zone-route"
                        onClick={() => setOpenThread(th.key)}
                      >
                        {t('Open the thread →')}
                      </button>
                    )}
                  </div>

                  {isOpen && subject?.r.detail && (
                    <>
                      <div className="ev" style={{ marginTop: 'var(--s12)' }}>{subject.r.detail}</div>
                      <div className="ev-src">
                        <Ref>{subject.r.label}</Ref><span className="sep">·</span>
                        <span>{t(REQUIREMENT_STATUS_LABEL[subject.r.status]?.word ?? subject.r.status)}</span><span className="sep">·</span>
                        <span>{t('the statement they are asking about')}</span>
                      </div>
                    </>
                  )}

                  {(isOpen ? th.items : th.items.slice(-1)).map((c) => (
                    <div className={`pa-msg${c.author_kind === 'operator' ? ' pa-msg--you' : ''}`} key={c.id}>
                      <div className="voice-record" style={{ whiteSpace: 'pre-wrap' }}>{c.body}</div>
                      <div className="ev-src">
                        <span>{c.author}</span>
                        <span className="badge badge-plain">{c.author_kind === 'operator' ? t('you') : t('client')}</span>
                        <span className="sep">·</span>
                        <span className="voice-figure">{fmtDateTime(c.created_at)}</span>
                      </div>
                    </div>
                  ))}

                  {isOpen && (
                    <div className="pa-reply">
                      <label className="u-label" htmlFor={`reply-${th.key}`} style={{ display: 'block', marginBottom: 6 }}>
                        {t('Reply as the consultant')}
                      </label>
                      <textarea
                        id={`reply-${th.key}`}
                        placeholder={t('This appears on {ref} in their portal.', { ref: th.ref ?? t('the object') })}
                        value={replyDraft[th.key] ?? ''}
                        onChange={(e) => setReplyDraft((d) => ({ ...d, [th.key]: e.target.value }))}
                      />
                      <div className="pa-reply-verbs">
                        <button
                          type="button"
                          className="btn btn-accent btn-sm"
                          disabled={replyBusy === th.key || !(replyDraft[th.key] ?? '').trim()}
                          onClick={() => void sendReply(th.object_type, th.object_id)}
                        >
                          {replyBusy === th.key ? t('Sending…') : t('Send')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => void openRoute(ROUTES.register({
                            engagement: engagementId ?? undefined,
                            ref: th.ref ?? undefined,
                          }))}
                        >
                          {t('Open it in the Register')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* ══ the staging bar — a floating layer, so it carries the page's one shadow ══ */}
      {staged.size > 0 && (
        <div className="pa-stagebar">
          <span className="pa-stage-what">
            {stageSummary.on > 0 && <>{t('share')} <b>{stageSummary.on}</b></>}
            {stageSummary.on > 0 && stageSummary.off > 0 && ' · '}
            {stageSummary.off > 0 && <>{t('stop sharing')} <b>{stageSummary.off}</b></>}
            {` — ${t('not written yet')}`}
          </span>
          <span className="pa-stage-warn">
            {[
              stageSummary.drafts ? tp(stageSummary.drafts, '{n} of them is still draft', '{n} of them are still draft') : '',
              stageSummary.exclusions ? tp(stageSummary.exclusions, '{n} is an exclusion', '{n} are exclusions') : '',
            ].filter(Boolean).join(' · ')}
          </span>
          <span className="pa-stage-verbs">
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setStaged(new Map())}>{t('Discard')}</button>
            <button
              type="button"
              className="btn btn-sm btn-accent"
              disabled={busy}
              onClick={() => setConfirmStage(true)}
            >
              {t('Publish these')}
            </button>
          </span>
        </div>
      )}

      {/* ══ dialogues ══ */}
      <ConfirmModal
        open={confirmStage}
        title={t('Change what the client can see')}
        confirmLabel={busy ? t('Writing…') : tp(staged.size, 'Write {n} change', 'Write {n} changes')}
        danger={stageSummary.drafts > 0 || stageSummary.exclusions > 0}
        onCancel={() => setConfirmStage(false)}
        onConfirm={() => void commitStaged()}
      >
        <p className="text-sm text-ink-2" style={{ lineHeight: 1.6 }}>
          {stageSummary.on > 0 && <>{t('Share')} <span className="voice-figure">{stageSummary.on}</span>{' '}</>}
          {stageSummary.on > 0 && stageSummary.off > 0 && `${t('and')} `}
          {stageSummary.off > 0 && <>{t('stop sharing')} <span className="voice-figure">{stageSummary.off}</span>{' '}</>}
          {t('for')} <Ref>{projection.engagement.code}</Ref>.
        </p>
        {stageSummary.drafts > 0 && (
          <p className="text-sm text-warn mt-8" style={{ lineHeight: 1.5 }}>
            <span className="voice-figure">{stageSummary.drafts}</span>{' '}
            {stageSummary.drafts === 1 ? t('of them is still') : t('of them are still')} <b>{t('draft')}</b> {t('in the register. A draft statement shown to a client reads as a commitment.')}
          </p>
        )}
        {stageSummary.exclusions > 0 && (
          <p className="text-sm text-warn mt-8" style={{ lineHeight: 1.5 }}>
            <span className="voice-figure">{stageSummary.exclusions}</span>{' '}
            {stageSummary.exclusions === 1 ? t('is descoped or explicitly out of scope') : t('are descoped or explicitly out of scope')}.
            {' '}{t('Showing an exclusion to a client is a contractual statement about what you are')} <em>{t('not')}</em>{' '}
            {t('doing — publish it only if you mean to make that point.')}
          </p>
        )}
      </ConfirmModal>

      <ConfirmModal
        open={confirmOff}
        title={t('Switch the portal off')}
        confirmLabel={busy ? t('Switching…') : t('Switch it off')}
        danger
        onCancel={() => setConfirmOff(false)}
        onConfirm={() => void setPortalEnabled(false)}
      >
        <p className="text-sm text-ink-2" style={{ lineHeight: 1.6 }}>
          {t('Every portal call for')} <Ref>{projection.engagement.code}</Ref> {t('is refused from the moment this is written — anyone signed in loses the engagement on their next click.')}{' '}
          <span className="voice-figure">{projection.shared.n}</span>{' '}
          {projection.shared.n === 1 ? t('published row') : t('published rows')} {t('and')}{' '}
          <span className="voice-figure">{projection.people.accounts}</span>{' '}
          {projection.people.accounts === 1 ? t('account') : t('accounts')} {t('stay exactly as they are and take effect again the moment it is switched back on.')}
        </p>
      </ConfirmModal>

      {newFrom && (
        <div className="modal-overlay" onClick={() => setNewFrom(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {newFrom.contact ? t('Portal account for {name}', { name: newFrom.contact.name }) : t('New portal user')}
              </h2>
              <button className="close-btn" onClick={() => setNewFrom(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label" htmlFor="pa-name">{t('Name')}</label>
                <input
                  id="pa-name" className="form-input" value={newFrom.name}
                  onChange={(e) => setNewFrom({ ...newFrom, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="pa-email">{t('Email')}</label>
                <input
                  id="pa-email" className="form-input mono" value={newFrom.email}
                  onChange={(e) => setNewFrom({ ...newFrom, email: e.target.value })}
                  placeholder="name@client.ch"
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="pa-role">{t('Role')}</label>
                <select
                  id="pa-role" className="form-select" value={newFrom.role}
                  onChange={(e) => setNewFrom({ ...newFrom, role: e.target.value as PortalRole })}
                >
                  <option value="reviewer">{t('reviewer — runs UAT, comments, raises defects')}</option>
                  <option value="sponsor">{t('sponsor — also signs the SOW, decides CRs, signs acceptance')}</option>
                </select>
              </div>
              <p className="text-xs text-ink-3" style={{ lineHeight: 1.6 }}>
                {t('The account is granted')} <Ref>{projection.engagement.code}</Ref> {t('straight away and cannot sign in until an invite is accepted and a password set.')}
                {newFrom.contact && ` ${t('The contact record is linked, so the two never drift apart.')}`}
              </p>
              {!enabled && (
                <p className="text-sm text-warn mt-8" style={{ lineHeight: 1.5 }}>
                  {t('The portal is off for this engagement, so the account will exist and reach nothing until the master switch is on.')}
                </p>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setNewFrom(null)}>{t('Cancel')}</button>
              <button
                className="btn btn-accent"
                disabled={busy || !newFrom.name.trim() || !newFrom.email.trim()}
                onClick={() => void createUser()}
              >
                {t('Create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {grantUser && (
        <div className="modal-overlay" onClick={() => setGrantUser(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('Engagement grants — {name}', { name: grantUser.name })}</h2>
              <button className="close-btn" onClick={() => setGrantUser(null)}>×</button>
            </div>
            <div className="modal-body">
              <p className="text-xs text-ink-3 mb-12">
                {t('A grant only covers engagements of {client}. Without one, the portal returns “not shared with you”.', { client: grantUser.client_name })}
              </p>
              {clientEngagements.map((e) => {
                const granted = detail?.users.find((u) => u.id === grantUser.id)
                  ?.grants.some((g) => g.engagement_id === e.id) ?? false;
                return (
                  <div key={e.id} className="flex items-center justify-between mb-8">
                    <div>
                      <Ref>{e.code}</Ref> <span className="text-sm text-ink-2">{e.name}</span>
                      <div className="text-xs text-ink-3">{t(e.current_phase)}</div>
                    </div>
                    <label className="toggle toggle-compact">
                      <input
                        className="toggle-input" type="checkbox" checked={granted}
                        onChange={(ev) => void toggleGrant(grantUser, e.id, ev.target.checked)}
                      />
                      <span className="toggle-slider" />
                    </label>
                  </div>
                );
              })}
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setGrantUser(null)}>{t('Done')}</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!deleteTarget}
        title={deleteTarget?.plan.allowed
          ? t('Delete {name}', { name: deleteTarget.user.name })
          : t('{name} cannot be deleted', { name: deleteTarget?.user.name ?? t('This account') })}
        confirmLabel={deleteTarget?.plan.allowed
          ? (removing ? t('Deleting…') : t('Delete permanently'))
          : deleteTarget?.user.disabled ? t('Already disabled') : t('Disable instead')}
        danger={!!deleteTarget?.plan.allowed}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => void removeUser()}
      >
        {deleteTarget && !deleteTarget.plan.allowed && deleteTarget.plan.blockers.map((b) => (
          <p className="text-sm text-ink-2" key={b.code} style={{ lineHeight: 1.6 }}>{b.message}</p>
        ))}
        {deleteTarget?.plan.allowed && (
          <>
            <p className="text-sm text-ink-2" style={{ lineHeight: 1.6 }}>
              {t('Delete')} <span className="text-ink-1">{deleteTarget.user.name}</span>{' '}
              (<span className="mono">{deleteTarget.user.email}</span>)? {t('This account has left no record behind, so it can simply go. An account that has signed, tested or raised anything is disabled instead.')}
            </p>
            {deleteTarget.plan.cascade.length > 0 && (
              <ul className="text-sm text-ink-2 mt-8" style={{ paddingLeft: 18, lineHeight: 1.7 }}>
                {deleteTarget.plan.cascade.map((c) => (
                  <li key={c.what}><span className="voice-figure">{c.count}</span> {c.what}</li>
                ))}
              </ul>
            )}
            <p className="text-xs text-ink-3 mt-8">
              {t('Any outstanding invite link dies with it. The audit log keeps the record.')}
            </p>
          </>
        )}
      </ConfirmModal>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// The miniature's body
// ────────────────────────────────────────────────────────────────────────────

/**
 * A MINIATURE OF THE PROJECTION, computed here — not a screenshot, and it says so in its own
 * footer. It exists so the operator can see the shape of the answer without spending a token,
 * and it is the one part of the page that is deliberately approximate about LAYOUT while being
 * exact about CONTENT: every row on it is a row the portal's own query would return.
 */
function PreviewBody({
  enabled, view, appearing, rowsFor,
}: {
  enabled: boolean;
  view: ProjectionView | null;
  appearing: ProjectionView[];
  rowsFor: (type: PortalObjectType) => { rows: PublishableRow[]; voice: Voice };
}) {
  const t = useT();
  if (!enabled) {
    // The sign-in screen, not a sentence about it — the sentence is already on the left, and a
    // fact appears exactly once.
    return (
      <div className="pv-signin">
        <div className="pv-flabel">{t('Email')}</div>
        <div className="pv-field" />
        <div className="pv-flabel" style={{ marginTop: 10 }}>{t('Password')}</div>
        <div className="pv-field" />
        <div className="pv-signbtn">{t('Sign in')}</div>
        <div className="pv-refused">{t('this engagement is not shared with you')}</div>
      </div>
    );
  }
  if (!view) return <div className="pv-quiet">{t('No view renders for this client.')}</div>;

  if (!view.needs) {
    const withRows = appearing.filter((v) => v.needs);
    return (
      <>
        <div className="pv-flabel">{t('The overview summarises the tabs below')}</div>
        {withRows.length ? (
          <div className="pv-quiet">
            {withRows.map((v) => `${t(v.label.toLowerCase())} ${v.published}`).join(' · ')}
          </div>
        ) : (
          <div className="pv-quiet">{t('Nothing has been published to you yet.')}</div>
        )}
      </>
    );
  }

  if (view.empty) {
    return (
      <div className="pv-empty">
        {t('This tab is in their navigation and it is empty.')}
        <span style={{ display: 'block', marginTop: 6, color: 'var(--ink-3)' }}>
          <span className="src-ref">{view.needs}</span> {t('is')} <b>{tCtx(t, view.visibility ?? '', 'access')}</b>,
          {' '}{t('0 of {total} rows published.', { total: view.total })}
        </span>
      </div>
    );
  }

  const { rows, voice } = rowsFor(view.needs);
  const shown = rows.slice(0, 4);
  return (
    <>
      {shown.map((r) => (
        <div className="pv-row" key={r.id}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
            {voice === 'record' && <span className={`att ${markOf(voice, r) ?? ''}`} />}
            <span className="pv-ref">{r.label}</span>
            {voice === 'record' && (r.status === 'draft' || r.status === 'proposed')
              && <span className="tag-unheld">{t(r.status)}</span>}
          </div>
          <div className={`pv-stmt${voice === 'record' ? ' pv-stmt--record' : voice === 'ref' ? ' pv-stmt--ref' : ''}`}>
            {(r.detail ?? '').slice(0, 150)}{(r.detail ?? '').length > 150 ? '…' : ''}
          </div>
        </div>
      ))}
      <div className="pv-more">
        {t('and')} <span className="voice-figure">{Math.max(0, view.published - shown.length)}</span> {t('more')} ·{' '}
        {t(view.capability)}
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// D9 — what one person could actually do
// ────────────────────────────────────────────────────────────────────────────

/**
 * Role × visibility × published rows, and nothing else. The sponsor-only set is fixed by
 * DESIGN §3 and enforced server-side (`/sows/:id/sign`, `/change-requests/:id/decide`,
 * `/acceptance-records/:id/sign`) — this line STATES it, it does not invent it.
 */
function capabilityOf(
  user: PortalUser,
  views: ProjectionView[],
  matrix: Record<PortalObjectType, Visibility>,
  t: T,
): React.ReactNode {
  if (user.disabled) return <span className="none">{t('nothing — the account is disabled')}</span>;
  if (!user.activated) return <span className="none">{t('nothing until the invite is accepted')}</span>;

  const parts: string[] = [];
  for (const v of views) {
    if (!v.appears || !v.needs || v.published === 0) continue;
    const surface = PORTAL_SURFACES[v.needs];
    if (matrix[v.needs] === 'read_act' && surface.act) {
      if (surface.sponsorOnly && user.role !== 'sponsor') {
        parts.push(t('{read} (only a sponsor acts)', { read: t(surface.read) }));
      } else {
        parts.push(t(surface.act));
      }
    } else {
      parts.push(t(surface.read));
    }
  }
  if (!parts.length) return <span className="none">{t('read the overview, and nothing else')}</span>;
  return parts.join(' · ');
}

/** Whole days between an ISO stamp and today, in the operator's timezone. */
function daysSince(iso: string | null): number {
  if (!iso) return 0;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return 0;
  const a = Date.UTC(then.getFullYear(), then.getMonth(), then.getDate());
  const now = new Date();
  const b = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.round((b - a) / 86_400_000));
}
